import type { Ref } from 'vue'
import {
  getLocalDayKey,
  getSeasonalEventInstanceKey,
} from '~/utils/seasonalWelcome'
import { findActiveSeasonalEvent } from '~/utils/seasonal/seasonalEventCalendar'
import type { SeasonalEventConfig } from '~/utils/seasonal/seasonalEventCalendar'
import {
  SEASONAL_DECOR_PREFS_COOKIE_NAME,
  coerceSeasonalDecorPrefsCookieToRawString,
  mirrorSeasonalDecorPrefsToStorage,
  parseDecorPrefsCookie,
  resolveRawSeasonalDecorPrefs,
} from '~/utils/seasonal/seasonalDecorPrefs'
import type { SeasonalDecorPrefs } from '~/utils/seasonal/seasonalDecorPrefs'
import {
  applyBodySeasonalAttributes,
  clearBodySeasonalAttributes,
  querySeasonalBrandLink,
} from '~/utils/seasonal/seasonalDecorDom'

export type { SeasonalDecorPrefs, SeasonalEventConfig }
export { findActiveSeasonalEvent }

const SEASONAL_HEADER_SHAKE_CLASS = 'seasonal-header-decor-wiggle'
const SEASONAL_HEADER_SHAKE_DURATION_MS = 480
const SEASONAL_HEADER_SHAKE_GAP_MIN_MS = 2800
const SEASONAL_HEADER_SHAKE_GAP_MAX_MS = 9800

const COOKIE_BASE = {
  sameSite: 'lax' as const,
  path: '/',
}

/** État partagé Nuxt (app + layout) */
function useSeasonalSharedState() {
  const activeEvent = useState<SeasonalEventConfig | null>('seasonal:active-event', () => null)
  const decorPrefs = useState<SeasonalDecorPrefs>('seasonal:decor-prefs', () => ({
    instanceKey: '',
    cursor: true,
    corner: true,
    logo: true,
  }))
  const showSeasonalWelcome = useState('seasonal:welcome-visible', () => false)
  const seasonalCursorReady = useState('seasonal:cursor-ready', () => false)
  /** true = la notice dev/recette doit passer avant toute ouverture auto de la modale festive */
  const deferSeasonalWelcomeAfterBeta = useState('seasonal:defer-welcome-after-beta', () => false)
  return {
    activeEvent,
    decorPrefs,
    showSeasonalWelcome,
    seasonalCursorReady,
    deferSeasonalWelcomeAfterBeta,
  }
}

type SeasonalWelcomeRuntime = {
  activeEvent: Ref<SeasonalEventConfig | null>
  decorPrefs: Ref<SeasonalDecorPrefs>
  showSeasonalWelcome: Ref<boolean>
  welcomeDayCookie: Ref<string | null>
  suppressCookie: Ref<string | null>
  prefsCookie: Ref<string | null>
}

function openSeasonalWelcomeModal(runtime: SeasonalWelcomeRuntime): void {
  const event = findActiveSeasonalEvent(new Date())
  if (!event) {
    return
  }
  runtime.activeEvent.value = event
  runtime.showSeasonalWelcome.value = true
}

function tryAutoOpenSeasonalWelcomeModal(runtime: SeasonalWelcomeRuntime): void {
  const event = findActiveSeasonalEvent(new Date())
  if (!event) {
    return
  }
  const instanceKey = getSeasonalEventInstanceKey(event)
  if (runtime.suppressCookie.value === instanceKey) {
    return
  }
  if (runtime.welcomeDayCookie.value === getLocalDayKey()) {
    return
  }
  runtime.activeEvent.value = event
  runtime.showSeasonalWelcome.value = true
}

function onSeasonalWelcomeConfirm(
  runtime: SeasonalWelcomeRuntime,
  payload: { suppressUntilNextEvent: boolean; prefs: SeasonalDecorPrefs },
): void {
  const event = runtime.activeEvent.value
  if (!event) {
    runtime.showSeasonalWelcome.value = false
    return
  }
  const instanceKey = getSeasonalEventInstanceKey(event)
  runtime.decorPrefs.value = { ...payload.prefs, instanceKey }
  const prefsJson = JSON.stringify(runtime.decorPrefs.value)
  runtime.prefsCookie.value = prefsJson
  mirrorSeasonalDecorPrefsToStorage(prefsJson)
  if (payload.suppressUntilNextEvent) {
    runtime.suppressCookie.value = instanceKey
  } else {
    runtime.suppressCookie.value = null
    runtime.welcomeDayCookie.value = getLocalDayKey()
  }
  runtime.showSeasonalWelcome.value = false
  applyBodySeasonalAttributes(runtime.decorPrefs.value)
}

function applyLiveSeasonalDecorUpdates(
  runtime: SeasonalWelcomeRuntime,
  updates: { cursor: boolean; corner: boolean; logo: boolean },
): void {
  if (!import.meta.client) {
    return
  }
  const event = runtime.activeEvent.value ?? findActiveSeasonalEvent(new Date())
  if (!event) {
    return
  }
  const instanceKey = getSeasonalEventInstanceKey(event)
  const nextPrefs: SeasonalDecorPrefs = {
    instanceKey,
    cursor: updates.cursor,
    corner: updates.corner,
    logo: updates.logo,
  }
  const previous = runtime.decorPrefs.value
  if (
    previous.instanceKey === nextPrefs.instanceKey &&
    previous.cursor === nextPrefs.cursor &&
    previous.corner === nextPrefs.corner &&
    previous.logo === nextPrefs.logo
  ) {
    return
  }
  runtime.decorPrefs.value = nextPrefs
  const prefsJson = JSON.stringify(runtime.decorPrefs.value)
  runtime.prefsCookie.value = prefsJson
  mirrorSeasonalDecorPrefsToStorage(prefsJson)
  applyBodySeasonalAttributes(runtime.decorPrefs.value)
}

export function useSeasonalWelcome() {
  const { activeEvent, decorPrefs, showSeasonalWelcome, deferSeasonalWelcomeAfterBeta } =
    useSeasonalSharedState()

  const welcomeDayCookie = useCookie<string | null>('seasonal_welcome_day', {
    ...COOKIE_BASE,
    maxAge: 60 * 60 * 24,
    default: () => null,
  })

  const suppressCookie = useCookie<string | null>('seasonal_welcome_suppress', {
    ...COOKIE_BASE,
    maxAge: 60 * 60 * 24 * 400,
    default: () => null,
  })

  const prefsCookie = useCookie<string | null>(SEASONAL_DECOR_PREFS_COOKIE_NAME, {
    ...COOKIE_BASE,
    maxAge: 60 * 60 * 24 * 400,
    default: () => null,
    watch: true,
  })

  const runtime: SeasonalWelcomeRuntime = {
    activeEvent,
    decorPrefs,
    showSeasonalWelcome,
    welcomeDayCookie,
    suppressCookie,
    prefsCookie,
  }

  return {
    activeSeasonalEvent: activeEvent,
    decorPrefs,
    deferSeasonalWelcomeAfterBeta,
    showSeasonalWelcome,
    openSeasonalWelcomeModal: () => openSeasonalWelcomeModal(runtime),
    tryAutoOpenSeasonalWelcomeModal: () => tryAutoOpenSeasonalWelcomeModal(runtime),
    onSeasonalWelcomeConfirm: (payload: { suppressUntilNextEvent: boolean; prefs: SeasonalDecorPrefs }) =>
      onSeasonalWelcomeConfirm(runtime, payload),
    applyLiveSeasonalDecorUpdates: (updates: { cursor: boolean; corner: boolean; logo: boolean }) =>
      applyLiveSeasonalDecorUpdates(runtime, updates),
  }
}

type SeasonalShakeTimers = {
  schedule: ReturnType<typeof setTimeout> | null
  clear: ReturnType<typeof setTimeout> | null
}

type SeasonalThemeState = {
  activeEvent: Ref<SeasonalEventConfig | null>
  decorPrefs: Ref<SeasonalDecorPrefs>
  seasonalCursorReady: Ref<boolean>
  deferSeasonalWelcomeAfterBeta: Ref<boolean>
  prefsCookie: Ref<string | null>
  isSeasonActive: Ref<boolean>
  cursorEmoji: Ref<string>
  cursorLeftPx: Ref<string>
  cursorTopPx: Ref<string>
  tryAutoOpenSeasonalWelcomeModal: () => void
  activeClassName: { value: string | null }
  listenerAttached: { value: boolean }
  onMouseMove: (mouseEvent: MouseEvent) => void
  shakeTimers: SeasonalShakeTimers
}

function onSeasonalMouseMove(state: SeasonalThemeState, mouseEvent: MouseEvent): void {
  state.cursorLeftPx.value = `${mouseEvent.clientX + 10}px`
  state.cursorTopPx.value = `${mouseEvent.clientY + 20}px`
  if (!state.seasonalCursorReady.value) {
    state.seasonalCursorReady.value = true
  }
}

function clearSeasonalHeaderShakeTimers(state: SeasonalThemeState): void {
  if (state.shakeTimers.schedule !== null) {
    clearTimeout(state.shakeTimers.schedule)
    state.shakeTimers.schedule = null
  }
  if (state.shakeTimers.clear !== null) {
    clearTimeout(state.shakeTimers.clear)
    state.shakeTimers.clear = null
  }
  querySeasonalBrandLink()?.classList.remove(SEASONAL_HEADER_SHAKE_CLASS)
}

function scheduleNextSeasonalHeaderShake(state: SeasonalThemeState): void {
  if (!state.decorPrefs.value.logo) {
    return
  }
  const gapRange = SEASONAL_HEADER_SHAKE_GAP_MAX_MS - SEASONAL_HEADER_SHAKE_GAP_MIN_MS
  const gapMs = SEASONAL_HEADER_SHAKE_GAP_MIN_MS + Math.random() * gapRange
  state.shakeTimers.schedule = window.setTimeout(() => {
    state.shakeTimers.schedule = null
    runSeasonalHeaderDecorShake(state)
  }, gapMs) as unknown as ReturnType<typeof setTimeout>
}

function runSeasonalHeaderDecorShake(state: SeasonalThemeState): void {
  if (!state.decorPrefs.value.logo) {
    return
  }
  const link = querySeasonalBrandLink()
  if (!link) {
    scheduleNextSeasonalHeaderShake(state)
    return
  }
  link.classList.add(SEASONAL_HEADER_SHAKE_CLASS)
  state.shakeTimers.clear = window.setTimeout(() => {
    link.classList.remove(SEASONAL_HEADER_SHAKE_CLASS)
    state.shakeTimers.clear = null
    scheduleNextSeasonalHeaderShake(state)
  }, SEASONAL_HEADER_SHAKE_DURATION_MS) as unknown as ReturnType<typeof setTimeout>
}

function attachSeasonalMouseMove(state: SeasonalThemeState): void {
  if (!import.meta.client || !state.isSeasonActive.value || !state.decorPrefs.value.cursor) {
    return
  }
  if (state.listenerAttached.value) {
    return
  }
  document.addEventListener('mousemove', state.onMouseMove)
  state.listenerAttached.value = true
}

function detachSeasonalMouseMove(state: SeasonalThemeState): void {
  if (!state.listenerAttached.value) {
    return
  }
  document.removeEventListener('mousemove', state.onMouseMove)
  state.listenerAttached.value = false
}

function startSeasonalHeaderShake(state: SeasonalThemeState): void {
  if (!import.meta.client || !state.isSeasonActive.value || !state.decorPrefs.value.logo) {
    return
  }
  void nextTick(() => {
    scheduleNextSeasonalHeaderShake(state)
  })
}

function onSeasonalCursorPrefChange(state: SeasonalThemeState, enabled: boolean): void {
  if (!import.meta.client || !state.isSeasonActive.value) {
    return
  }
  if (enabled) {
    attachSeasonalMouseMove(state)
  } else {
    detachSeasonalMouseMove(state)
    state.seasonalCursorReady.value = false
  }
  applyBodySeasonalAttributes(state.decorPrefs.value)
}

function onSeasonalLogoPrefChange(state: SeasonalThemeState, enabled: boolean): void {
  if (!import.meta.client || !state.isSeasonActive.value) {
    return
  }
  if (enabled) {
    startSeasonalHeaderShake(state)
  } else {
    clearSeasonalHeaderShakeTimers(state)
  }
  applyBodySeasonalAttributes(state.decorPrefs.value)
}

function onSeasonalCornerPrefChange(state: SeasonalThemeState): void {
  if (import.meta.client && state.isSeasonActive.value) {
    applyBodySeasonalAttributes(state.decorPrefs.value)
  }
}

function restoreSeasonalPrefsCookie(state: SeasonalThemeState, rawPrefs: string | null): void {
  if (!import.meta.client || !rawPrefs) {
    return
  }
  const fromComposable = coerceSeasonalDecorPrefsCookieToRawString(state.prefsCookie.value) ?? ''
  if (fromComposable.length === 0) {
    state.prefsCookie.value = rawPrefs
  }
}

async function mountSeasonalTheme(state: SeasonalThemeState): Promise<void> {
  const event = findActiveSeasonalEvent(new Date())
  if (!event) {
    state.activeEvent.value = null
    return
  }

  const instanceKey = getSeasonalEventInstanceKey(event)
  const rawPrefs = resolveRawSeasonalDecorPrefs(state.prefsCookie.value)
  restoreSeasonalPrefsCookie(state, rawPrefs)
  state.decorPrefs.value = parseDecorPrefsCookie(rawPrefs, instanceKey)
  state.activeEvent.value = event
  state.activeClassName.value = event.className
  document.body.classList.add(event.className)
  applyBodySeasonalAttributes(state.decorPrefs.value)
  state.cursorEmoji.value = event.emoji
  state.isSeasonActive.value = true

  if (state.decorPrefs.value.cursor) {
    attachSeasonalMouseMove(state)
  }
  if (state.decorPrefs.value.logo) {
    await nextTick()
    scheduleNextSeasonalHeaderShake(state)
  }
  await nextTick()
  if (!state.deferSeasonalWelcomeAfterBeta.value) {
    state.tryAutoOpenSeasonalWelcomeModal()
  }
}

function unmountSeasonalTheme(state: SeasonalThemeState): void {
  clearSeasonalHeaderShakeTimers(state)
  detachSeasonalMouseMove(state)
  if (state.activeClassName.value) {
    document.body.classList.remove(state.activeClassName.value)
    state.activeClassName.value = null
  }
  clearBodySeasonalAttributes()
  state.activeEvent.value = null
  state.isSeasonActive.value = false
  state.cursorEmoji.value = ''
  state.seasonalCursorReady.value = false
}

export function useSeasonalTheme() {
  const { activeEvent, decorPrefs, seasonalCursorReady, deferSeasonalWelcomeAfterBeta } =
    useSeasonalSharedState()
  const { tryAutoOpenSeasonalWelcomeModal } = useSeasonalWelcome()

  const prefsCookie = useCookie<string | null>(SEASONAL_DECOR_PREFS_COOKIE_NAME, {
    ...COOKIE_BASE,
    maxAge: 60 * 60 * 24 * 400,
    default: () => null,
    watch: true,
  })

  const isSeasonActive = ref(false)
  const cursorEmoji = ref('')
  const cursorLeftPx = ref('0px')
  const cursorTopPx = ref('0px')

  const state: SeasonalThemeState = {
    activeEvent,
    decorPrefs,
    seasonalCursorReady,
    deferSeasonalWelcomeAfterBeta,
    prefsCookie,
    isSeasonActive,
    cursorEmoji,
    cursorLeftPx,
    cursorTopPx,
    tryAutoOpenSeasonalWelcomeModal,
    activeClassName: { value: null },
    listenerAttached: { value: false },
    onMouseMove: function ignoreMove() {},
    shakeTimers: { schedule: null, clear: null },
  }
  state.onMouseMove = function onMove(mouseEvent: MouseEvent) {
    onSeasonalMouseMove(state, mouseEvent)
  }

  watch(
    () => decorPrefs.value.cursor,
    function onCursorPref(enabled: boolean) {
      onSeasonalCursorPrefChange(state, enabled)
    },
  )
  watch(
    () => decorPrefs.value.logo,
    function onLogoPref(enabled: boolean) {
      onSeasonalLogoPrefChange(state, enabled)
    },
  )
  watch(
    () => decorPrefs.value.corner,
    function onCornerPref() {
      onSeasonalCornerPrefChange(state)
    },
  )

  const showSeasonalCursor = computed(
    () =>
      isSeasonActive.value &&
      decorPrefs.value.cursor &&
      seasonalCursorReady.value,
  )

  onMounted(function onSeasonalThemeMounted() {
    void mountSeasonalTheme(state)
  })
  onBeforeUnmount(function onSeasonalThemeUnmounted() {
    unmountSeasonalTheme(state)
  })

  return {
    isSeasonActive,
    cursorEmoji,
    cursorLeftPx,
    cursorTopPx,
    showSeasonalCursor,
    decorPrefs,
  }
}
